// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import validateEmail from "@insite/client-framework/Common/Utilities/validateEmail";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import subscribe from "@insite/client-framework/Store/CommonHandlers/Subscribe";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { ReCaptchaStyles } from "@insite/content-library/Components/ReCaptcha";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Radio from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupProps } from "@insite/mobius/RadioGroup";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@rpsc/App/Store/Reducers";
import { getSettingsCollection } from "@rpsc/App/Store/Selectors/SettingsSelectors";
import ReCaptcha from "@rpsc/Overrides/Components/ReCaptcha";
import * as cssLinter from "css";
import parse from "html-react-parser";
import React, { RefObject, useContext, useEffect, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";

const enum fields {
    label = "label",
    // XNG-Changes: hide title field and use siteMessage instead
    // title = "title",
    description = "description",
    placeholder = "placeholder",
    disclaimer = "disclaimer",
    alignment = "alignment",
    customCSS = "customCSS",
    // XNG-changes: hide fields for now as we're only using one general mailing list
    // firstEmail = "firstEmail",
    // secondEmail = "secondEmail",
    // thirdEmail = "thirdEmail",
}

interface OwnProps extends WidgetProps {
    fields: {
        // [fields.title]: string;
        [fields.label]: string;
        [fields.description]: string;
        [fields.placeholder]: string;
        [fields.disclaimer]: string;
        [fields.alignment]: string;
        [fields.customCSS]: string;
        // XNG-Changes
        // [fields.firstEmail]: string;
        // [fields.secondEmail]: string;
        // [fields.thirdEmail]: string;
    };
}

const mapDispatchToProps = {
    subscribe,
    // XNG-Changes
    validateReCaptcha: makeHandlerChainAwaitable<{ location: string; recaptchaRef: RefObject<ReCAPTCHA> }, boolean>(
        validateReCaptcha,
    ),
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export interface SubscribeStyles {
    mainGridContainer: GridContainerProps;
    titleGridItem?: GridItemProps;
    titleLabel?: TypographyPresentationProps;
    descriptionGridItem?: GridItemProps;
    descriptionText?: TypographyPresentationProps;
    emailGridItem?: GridItemProps;
    emailTextField?: TextFieldPresentationProps;
    emailButton?: ButtonPresentationProps;
    disclaimerGridItem?: GridItemProps;
    disclaimerText?: TypographyPresentationProps;
    // XNG-Changes
    mainContainer?: InjectableCss;
    topSectionWrapper?: InjectableCss;
    infoWrapper?: InjectableCss;
    emailWrapper?: InjectableCss;
    emailsRadioGroup?: RadioGroupProps;
    recaptchaWrapper?: InjectableCss;
    recaptchaStyle?: ReCaptchaStyles;
}

export const subscribeStyles: SubscribeStyles = {
    mainGridContainer: {
        gap: 10,
        css: css`
            width: 100%;
            display: flex;
        `,
    },
    titleGridItem: {
        width: 12,
    },
    titleLabel: {
        variant: "h4",
        css: css`
            margin-bottom: -5px;
        `,
    },
    descriptionGridItem: {
        width: 12,
    },
    descriptionText: {
        css: css`
            margin-bottom: -1rem;
        `,
    },
    emailGridItem: {
        width: 12,
    },
    emailTextField: {
        cssOverrides: {
            formField: css`
                max-width: 300px;
                margin-right: 10px;
            `,
        },
    },
    emailButton: {
        css: css`
            width: 200px;
        `,
    },
    disclaimerGridItem: {
        width: 12,
    },
    disclaimerText: {
        css: css`
            font-size: 12px;
        `,
    },
};

const defaultCustomCss = `.main-grid-container{
}

.title-grid-item{
}

.title{
}

.description-grid-item{
}

.description-text{
}

.email-grid-item{
}

.email-text-field{
}

.email-button{
}

.disclaimer-grid-item{
}

.disclaimer-text{
}`;

export const CmsSubscribe = ({ fields, subscribe, id, validateReCaptcha }: Props) => {
    const emailRequiredFieldMessage = siteMessage("EmailSubscription_EmailIsRequiredErrorMessage");
    const emailFieldMessage = siteMessage("EmailSubscription_EmailIsInvalidErrorMessage");

    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = useState(emailRequiredFieldMessage);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const toasterContext = useContext(ToasterContext);
    // XNG-Changes
    const recaptchaRef: RefObject<ReCAPTCHA> = React.createRef();
    const dispatch = useDispatch();

    const styles = useMergeStyles("subscribe", subscribeStyles);

    // XNG-Changes
    const { enableZohoRecaptcha } = useAppSelector(state => ({
        enableZohoRecaptcha:
            getSettingsCollection(state).xngSecuritySettings.checkReCaptchaForZohoSubscribeOnClientSide,
    }));

    const onSubscribeClick = async () => {
        setIsSubmitted(true);
        if (!checkEmail(email)) {
            return;
        }

        // XNG-Changes: validateRecaptcha
        const isReCaptchaValid = await validateReCaptcha({ location: "ZohoSubscribe", recaptchaRef });
        if (!isReCaptchaValid) {
            return;
        }

        subscribe({
            email,
            onSuccess: () => {
                toasterContext.addToast({ body: siteMessage("Email_Subscribe_Success"), messageType: "success" });
            },
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.();
                }
            },
        });
    };

    const emailChangeHandler = (email: string) => {
        checkEmail(email);
        setEmail(email);
    };

    const checkEmail = (email: string) => {
        const errorMessage = !email ? emailRequiredFieldMessage : validateEmail(email) ? "" : emailFieldMessage;
        setEmailError(errorMessage);
        return !errorMessage;
    };

    const gridItemCss = (gridItemProps?: GridItemProps) => {
        return css`
            display: flex;
            justify-content: ${fields.alignment ? fields.alignment : "center"};
            ${gridItemProps?.css}
        `;
    };

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    // const emailsCount = (fields.firstEmail ? 1 : 0) + (fields.secondEmail ? 1 : 0) + (fields.thirdEmail ? 1 : 0);

    /**
     * XNG-Changes: Update Structure, use siteMessage for title and add emailList radioGroup
     */
    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledWrapper {...styles.mainContainer} className="main-grid-container">
                <StyledWrapper {...styles.topSectionWrapper}>
                    <StyledWrapper {...styles.infoWrapper}>
                        <StyledWrapper
                            className="title-grid-item"
                            {...styles.titleGridItem}
                            css={gridItemCss(styles.titleGridItem)}
                        >
                            <Typography className="title" {...styles.titleLabel}>
                                {siteMessage("Custom_Subscribe_Title")}
                            </Typography>
                        </StyledWrapper>
                        <StyledWrapper
                            className="description-grid-item"
                            {...styles.descriptionGridItem}
                            css={gridItemCss(styles.descriptionGridItem)}
                        >
                            <Typography className="description-text" {...styles.descriptionText}>
                                {parse(fields.description, parserOptions)}
                            </Typography>
                        </StyledWrapper>
                        {fields.disclaimer && (
                            <StyledWrapper
                                className="disclaimer-grid-item"
                                {...styles.disclaimerGridItem}
                                css={gridItemCss(styles.disclaimerGridItem)}
                            >
                                <Typography className="disclaimer-text" {...styles.disclaimerText}>
                                    {parse(fields.disclaimer, parserOptions)}
                                </Typography>
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                    <StyledWrapper
                        {...styles.emailWrapper}
                        css={gridItemCss(styles.emailWrapper)}
                        className="email-grid-item"
                    >
                        <TextField
                            className="email-text-field"
                            {...styles.emailTextField}
                            id={id}
                            placeholder={fields.placeholder}
                            value={email}
                            onChange={e => emailChangeHandler(e.currentTarget.value)}
                            error={isSubmitted && emailError}
                        />
                        <Button
                            className="email-button"
                            {...styles.emailButton}
                            onClick={onSubscribeClick}
                            disabled={isSubmitted && !!emailError}
                            aria-label={fields.label}
                        >
                            {fields.label}
                        </Button>
                    </StyledWrapper>
                </StyledWrapper>
                {enableZohoRecaptcha && (email || isSubmitted) && (
                    <StyledWrapper {...styles.recaptchaWrapper}>
                        <ReCaptcha
                            extendedStyles={styles.recaptchaStyle}
                            recaptchaRef={recaptchaRef}
                            location="ZohoSubscribe"
                            forceEnable={enableZohoRecaptcha}
                        />
                    </StyledWrapper>
                )}
                {/* XNG-Changes: hide this section as for now we're only using one general mailing list */}
                {/* {emailsCount > 1 && (
                    <StyledWrapper {...styles.emailsListWrapper}>
                        <RadioGroup
                            horizontal={!isMobileScreen}
                            label="Request to Receive Emails for:"
                            value={fields.firstEmail || fields.secondEmail || fields.thirdEmail}
                            {...styles.emailsRadioGroup}
                            // -- TODO --
                            // onChangeHandler={}
                        >
                            {!!fields.firstEmail && <Radio value={fields.firstEmail}>{fields.firstEmail}</Radio>}
                            {!!fields.secondEmail && <Radio value={fields.secondEmail}>{fields.secondEmail}</Radio>}
                            {!!fields.thirdEmail && <Radio value={fields.thirdEmail}>{fields.thirdEmail}</Radio>}
                        </RadioGroup>
                    </StyledWrapper>
                )} */}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(CmsSubscribe),
    definition: {
        group: "Basic",
        icon: "bell",
        fieldDefinitions: [
            {
                name: fields.alignment,
                displayName: "Layout",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "flex-start" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "flex-end" },
                ],
                defaultValue: "center",
                fieldType: "Translatable",
                tab: basicTab,
            },
            // XNG-Changes: hide title field
            // {
            //     name: fields.title,
            //     displayName: "Title",
            //     editorTemplate: "TextField",
            //     defaultValue: "Subscribe",
            //     fieldType: "Translatable",
            //     tab: basicTab,
            // },
            {
                name: fields.description,
                displayName: "Description",
                editorTemplate: "RichTextField",
                defaultValue: "<p>Keep up-to-date on product news and the latest offers.</p>",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: fields.placeholder,
                displayName: "Placeholder Text",
                editorTemplate: "TextField",
                defaultValue: "Enter email address",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: fields.disclaimer,
                displayName: "Disclaimer",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: fields.label,
                displayName: "Button Label",
                editorTemplate: "TextField",
                defaultValue: "Subscribe",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
            // XNG-Changes
            // {
            //     name: fields.firstEmail,
            //     editorTemplate: "TextField",
            //     defaultValue: "",
            //     fieldType: "Translatable",
            //     tab: basicTab,
            // },
            // {
            //     name: fields.secondEmail,
            //     editorTemplate: "TextField",
            //     defaultValue: "",
            //     fieldType: "Translatable",
            //     tab: basicTab,
            // },
            // {
            //     name: fields.thirdEmail,
            //     editorTemplate: "TextField",
            //     defaultValue: "",
            //     fieldType: "Translatable",
            //     tab: basicTab,
            // },
        ],
    },
};

export default widgetModule;
